/**
 * Appstle Per-Gram Dynamic Pricing
 * 
 * Reads product weight from Liquid-rendered JSON data tags,
 * calculates per-gram price, and injects it into Appstle widgets.
 * Works on product pages, homepage featured products, and any
 * page where the Appstle widget + per-gram data snippet are present.
 */
(function () {
  'use strict';

  // ── Helpers ──────────────────────────────────────────────

  /**
   * Parse a dollar amount from direct text nodes of an element
   * (ignores child elements like existing .Size divs)
   */
  function parsePriceFromEl(el) {
    var text = '';
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === Node.TEXT_NODE) {
        text += el.childNodes[i].textContent;
      }
    }
    var match = text.match(/\$([\d,.]+)/);
    return match ? parseFloat(match[1].replace(',', '')) : null;
  }

  /**
   * Inject or update a .Size per-gram div inside a price element
   */
  function setPerGram(priceEl, price, weightGrams) {
    if (!price || !weightGrams || weightGrams <= 0) return;

    var perGram = (price / weightGrams).toFixed(2);
    var existing = priceEl.querySelector('.Size');

    if (existing) {
      existing.textContent = '$' + perGram + '/gram';
    } else {
      var div = document.createElement('div');
      div.className = 'Size';
      div.textContent = '$' + perGram + '/gram';
      priceEl.appendChild(div);
    }
  }

  // ── Core update logic for a single widget ────────────────

  function updateWidget(widget, weightGrams) {
    // --- One-time purchase ---
    var otpWrapper = widget.querySelector('.appstle_one_time_price_wrapper');
    if (otpWrapper) {
      // The visible price element (not sr-only)
      var otpPriceEl = otpWrapper.querySelector(
        '.appstle_subscription_amount:not([class*="sr-only"])'
      );
      var otpPrice = otpPriceEl ? parsePriceFromEl(otpPriceEl) : null;

      // If the one-time price span is empty (Appstle sometimes leaves it blank),
      // fall back to the compare-at price which holds the full OTP price
      if (!otpPrice) {
        var compareEl = otpWrapper.querySelector(
          '.appstle_subscription_amount_compare_at_price:not([class*="sr-only"])'
        );
        otpPrice = compareEl ? parsePriceFromEl(compareEl) : null;

        // Inject per-gram into the main price span (even if price text is empty,
        // the div will appear in the right spot)
        if (otpPriceEl && otpPrice) {
          setPerGram(otpPriceEl, otpPrice, weightGrams);
        }
      } else if (otpPriceEl) {
        setPerGram(otpPriceEl, otpPrice, weightGrams);
      }
    }

    // --- Subscription price ---
    var subWrapper = widget.querySelector('.appstle_subscription_amount_wrapper');
    if (subWrapper) {
      var subPriceEl = subWrapper.querySelector(
        '.appstle_subscription_amount:not([class*="sr-only"])'
      );
      if (subPriceEl) {
        var subPrice = parsePriceFromEl(subPriceEl);
        if (subPrice) {
          setPerGram(subPriceEl, subPrice, weightGrams);
        }
      }
    }
  }

  // ── Find product handle for a given widget ───────────────

  function getHandleForWidget(widget) {
    // Method 1: standalone selector inside the same form
    var standalone = widget.closest('form')
      ? widget.closest('form').querySelector('.appstle_stand_alone_selector[data-product-handle]')
      : null;
    if (standalone) return standalone.dataset.productHandle;

    // Method 2: standalone selector walking up from widget
    var parent = widget.parentElement;
    while (parent && parent !== document.body) {
      var sel = parent.querySelector('.appstle_stand_alone_selector[data-product-handle]');
      if (sel) return sel.dataset.productHandle;
      parent = parent.parentElement;
    }

    // Method 3: product page URL (prioritize over DOM link traversal
    // because upsell/cross-sell links in the same section can mislead)
    var pageMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (pageMatch) return pageMatch[1];

    // Method 4: fallback to product URL from a link near the widget
    // (for non-product pages like homepage featured sections)
    parent = widget.parentElement;
    while (parent && parent !== document.body) {
      var link = parent.querySelector('a[href*="/products/"]');
      if (link) {
        var m = link.href.match(/\/products\/([^/?#]+)/);
        if (m) return m[1];
      }
      parent = parent.parentElement;
    }

    return null;
  }

  // ── Build handle → weight map from Liquid JSON tags ──────

  function buildWeightMap() {
    var map = {};
    var tags = document.querySelectorAll('script.appstle-per-gram-data');
    for (var i = 0; i < tags.length; i++) {
      try {
        var data = JSON.parse(tags[i].textContent);
        if (data.handle && data.net_weight_grams) {
          map[data.handle] = data.net_weight_grams;
        }
      } catch (e) {
        // skip malformed JSON
      }
    }
    return map;
  }

  // ── Process all widgets on the page ──────────────────────

  function processAllWidgets() {
    var weightMap = buildWeightMap();
    var widgets = document.querySelectorAll('.appstle_sub_widget');

    for (var i = 0; i < widgets.length; i++) {
      var handle = getHandleForWidget(widgets[i]);
      if (handle && weightMap[handle]) {
        updateWidget(widgets[i], weightMap[handle]);
      }
    }
  }

  // ── Initialize with MutationObserver ─────────────────────

  function init() {
    // Run immediately in case widgets are already rendered
    processAllWidgets();

    // Watch for Appstle widgets loading asynchronously
    var observer = new MutationObserver(function (mutations) {
      var shouldUpdate = false;
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (
          target.classList &&
          (target.classList.contains('appstle_sub_widget') ||
            target.classList.contains('appstle_subscription_amount') ||
            target.classList.contains('appstle_subscription_amount_wrapper') ||
            target.classList.contains('appstle_one_time_price_wrapper'))
        ) {
          shouldUpdate = true;
          break;
        }
        // Check added nodes
        if (mutations[i].addedNodes.length) {
          for (var j = 0; j < mutations[i].addedNodes.length; j++) {
            var node = mutations[i].addedNodes[j];
            if (node.nodeType === 1 && (
              node.classList.contains('appstle_sub_widget') ||
              node.querySelector && node.querySelector('.appstle_sub_widget')
            )) {
              shouldUpdate = true;
              break;
            }
          }
        }
        if (shouldUpdate) break;
      }
      if (shouldUpdate) {
        processAllWidgets();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Listen for plan changes (dropdown & radio toggles)
    document.addEventListener('change', function (e) {
      if (
        e.target.classList.contains('appstle_select') ||
        e.target.name === 'selling_plan'
      ) {
        // Small delay for Appstle to update prices
        setTimeout(processAllWidgets, 250);
      }
    });
  }

  // ── Kick off ─────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();